import {ComboboxItem, ComboboxProvider, SelectProvider} from '@ariakit/react'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectTrigger,
    SelectVirtualList
} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {StyledSelectItem} from '@components/commons/multi-select-checkbox/style.ts'
import {Checkbox} from '@components/commons/checkbox/ChekBox.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {FC} from 'react'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {Skeleton} from '@components/ui/skeleton/Skeleton.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {useNeuromuscularDiagnosis} from '@/features/patients/services/useNeuromuscularDiagnosis'

interface NeuromuscularDiagnosisFilterProps {
    value: string[]
    onChange: (value: string[]) => void
}

export const NeuromuscularDiagnosisFilter: FC<NeuromuscularDiagnosisFilterProps> = ({value, onChange}) => {
    const {t} = useTranslation()
    const query = useNeuromuscularDiagnosis({
        limit: 20,
        responseType: 'small'
    })

    return (
        <ComboboxProvider setValue={query.onSearch} resetValueOnHide>
            <SelectProvider setValue={onChange} value={value} defaultValue={[]}>
                <SelectTrigger
                    displayValue={value => t('commons:x_selected', {count: value.length})}
                    placeholder={t('table_filters:select_value')}
                />
                <SelectComboboxPopover style={{width: '360px'}} sameWidth={false}>
                    <SelectComboboxInput />
                    <SelectComboboxList>
                        {query.isLoading ? (
                            <Flexbox direction="column" gap={1}>
                                {Array.from({length: 6}, (_, index) => (
                                    <Skeleton key={index} height={32} />
                                ))}
                            </Flexbox>
                        ) : query.isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : query.remappedData.length == 0 ? (
                            <InputHelpText helpText={t('commons:no_results')} />
                        ) : (
                            <SelectVirtualList
                                data={query.remappedData}
                                totalCount={query.remappedData.length}
                                overscan={100}
                                endReached={query.fetchNextPage}
                                itemContent={(_, option) => (
                                    <StyledSelectItem
                                        render={<ComboboxItem />}
                                        value={option.id}
                                        key={option.id}
                                        selectItemContentClassName="content"
                                    >
                                        {option.name}
                                        <Checkbox
                                            id="selected"
                                            checked={value.includes(option.id)}
                                            onChange={() => undefined}
                                        />
                                    </StyledSelectItem>
                                )}
                            />
                        )}
                    </SelectComboboxList>
                </SelectComboboxPopover>
            </SelectProvider>
        </ComboboxProvider>
    )
}
